import _isEmpty from 'lodash/isEmpty';

import { Link, navigate } from '@reach/router';
import Cookies from 'js-cookie';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import FormPost from 'components/common/form-saml-response/formPost';
import {
    CommonContextV2,
    CommonLayoutV2,
    CustomButtonV2,
    CustomInputV2,
    CustomSnackbarV2,
    CustomTotpInputV2
} from 'components/v2';

import usePageMetadata from 'hooks/usePageMetadata';
import {
    COMMON_ERROR_MESSAGE,
    COOKIE_ERROR_MESSAGE,
    errorMessageMap,
    ERRORS_CODE,
    EXCLUDED_QUERY_PARAMS,
    NETWORK_ERROR_MESSAGE,
    PAYLOAD_COOKIE,
    REFRESH_COOKIE,
    SESSION_COOKIE
} from 'services/Constant';
import { logService } from 'services/LogService';
import QueryParametersParser from 'services/QueryParametersParser';
import Validator from 'services/Validator';
import { axios } from 'services/axios';
import gtmService from 'services/gtmService';
import idpErrors from 'services/idpErrors';
import organizationErrors from 'services/organizationErrors';
import productData from 'services/productData';
import { errorTraceID, getAppNameByID, isWhiteListed } from 'services/utility';

import './login.scss';

const enableLogging = ['dev', 'qa', 'local', 'staging'].includes(process.env.REACT_APP_ENVIRONMENT);

const LoginPageV2 = ({ location }) => {
    const { query, appId, appName } = useContext(CommonContextV2);

    const isInQA = useMemo(
        () =>
            query?.inQA === 'true' &&
            (process.env.REACT_APP_ENVIRONMENT === 'dev' ||
                process.env.REACT_APP_ENVIRONMENT === 'qa' ||
                process.env.REACT_APP_ENVIRONMENT === 'local'),
        [query]
    );

    const autoLogin = useMemo(
        () =>
            (query?.app === process.env.REACT_APP_appID_MA || query?.app === process.env.REACT_APP_appID_Fusion) &&
            query?.token,
        [query]
    );

    const recaptchaRef = useRef(null);

    const [email, setEmail] = useState(location?.state?.activatedEmail || location?.state?.email || '');
    const [password, setPassword] = useState('');
    const [checkedEmail, setCheckedEmail] = useState(location?.state?.activatedEmail || '');
    const [captchaResponse, setCaptchaResponse] = useState();
    const [remainingTries, setRemainingTries] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isHandlingPostLogin, setIsHandlingPostLogin] = useState(false);
    const [isLoadingCurrentUser, setIsLoadingCurrentUser] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [infoMessages, setInfoMessages] = useState([]);
    const [highlightErrors, setHighlightErrors] = useState(false);

    const [samlRequest, setSamlRequest] = useState();
    const [relayState, setRelayState] = useState();
    const [samlResponse, setSamlResponse] = useState();
    const [entityEndpoint, setEntityEndpoint] = useState();
    const [samlIdpRequest, setSamlIdpRequest] = useState();
    const [responseData, setResponseData] = useState(undefined);
    const [isTotpChallenge, setIsTotpChallenge] = useState(false);
    const [totp, setTotp] = useState('');
    const [isInputRecoveryCode, setIsInputRecoveryCode] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [showMfaDisableConfirm, setShowMfaDisableConfirm] = useState(false);
    const [showEmailSent, setShowEmailSent] = useState(false);
    const [mfaDisableAgreed, setMfaDisableAgreed] = useState(false);

    const emailErrors = useMemo(() => {
        if (email && !Validator.isEmail(email)) {
            return ['Invalid Email Address'];
        }
        return [];
    }, [email]);

    // The user has attempted to login twice and only have one remaining attempt to login before it locks the account
    const lastTryInGeneral = localStorage.getItem('loginAttempt') > 2;
    const lastTryByEmail = remainingTries < 2;
    const isAfterTwoTries = lastTryInGeneral || lastTryByEmail;

    const clearCookie = () => {
        let cookiesOptions = {
            path: '/',
            domain: '.opswat.com',
            expires: 0
        };
        Cookies.set(SESSION_COOKIE, '', cookiesOptions);
        Cookies.set(REFRESH_COOKIE, '', cookiesOptions);
        Cookies.set(PAYLOAD_COOKIE, '', cookiesOptions);
    };

    const clearEmail = (e) => {
        e.preventDefault();
        setCheckedEmail('');
    };

    const postLoginToParent = (messageData) => {
        // check if webpage is being used in an iframe
        const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
        if (isInIframe && document.location.ancestorOrigins) {
            let parentOrigin = document.location.ancestorOrigins[0];
            window.parent.postMessage(messageData, parentOrigin);
        }
    };

    const checkCurrentUser = (samlRequest, appId, relayState) => {
        setIsLoadingCurrentUser(true);
        (async () => {
            try {
                const request = await axios.get('/current-user', {
                    params: { SAMLRequest: samlRequest, appId, RelayState: relayState }
                });
                if (request.status === 200 && request.data) {
                    // If user not press login button yet, accept current user data
                    setResponseData((previous) => (previous === undefined ? request.data : previous));
                    setIsLoadingCurrentUser(false);
                }
            } catch (err) {
                // Payload Too Large
                if (err?.response?.status === 413) {
                    clearCookie();
                    setErrorMessages([
                        {
                            title: 'Authentication Failed.',
                            err,
                            messages: [COOKIE_ERROR_MESSAGE]
                        }
                    ]);
                } else {
                    setErrorMessages([
                        {
                            title: err?.response?.status === 403 ? 'Access Denied.' : 'Authentication Failed.',
                            err,
                            messages: [err?.response?.data?.error?.message || NETWORK_ERROR_MESSAGE]
                        }
                    ]);
                }
                logService.error(err);
                setIsLoadingCurrentUser(false);
            }
        })();
    };

    const onCheckEmail = (emailToCheck, href) => {
        setIsLoading(true);
        (async () => {
            try {
                const response = await axios.post('/IDP/lookup', { email: emailToCheck, location: href });
                if (response.status === 200 && response.data) {
                    if (response.data.type === 'HTTP-Redirect' && response.data.location) {
                        // Redirect to IDP
                        navigate(response.data.location);
                    } else if (
                        response.data.type === 'HTTP-POST' &&
                        response.data.location &&
                        response.data.SAMLRequest
                    ) {
                        // Post request to IDP
                        // Set POST endpoint
                        setEntityEndpoint(response.data.location);
                        // Create POST data
                        const requestInputs = [
                            {
                                name: 'SAMLRequest',
                                value: response.data.SAMLRequest
                            }
                        ];
                        if (response.data.RelayState) {
                            requestInputs.push({
                                name: 'RelayState',
                                value: response.data.RelayState
                            });
                        }
                        setSamlIdpRequest(requestInputs);
                    }
                    return;
                }
            } catch (error) {
                const status = error?.response?.status;
                if (status === 404) {
                    setCheckedEmail(emailToCheck);
                } else {
                    logService.error(error);
                    // Payload Too Large
                    if (status === 413) {
                        clearCookie();
                        setErrorMessages([
                            {
                                title: 'Authentication Failed.',
                                error,
                                messages: [COOKIE_ERROR_MESSAGE]
                            }
                        ]);
                    } else {
                        setErrorMessages([
                            {
                                title: status === 403 ? 'Access Denied.' : 'Authentication Failed.',
                                error,
                                messages: [error?.response?.data?.message || NETWORK_ERROR_MESSAGE]
                            }
                        ]);
                    }
                }
            }
            setIsLoading(false);
        })();
    };

    const onSubmit = useMemo(
        () => (totp, recovery) => {
            if (!email || !password || (!captchaResponse && isAfterTwoTries && !isInQA)) {
                return;
            }

            // Set response data to 'false', to discard checkCurrentUser result
            setResponseData(false);
            setIsLoading(true);
            setErrorMessages([]);
            setInfoMessages([]);
            setHighlightErrors(false);

            (async () => {
                try {
                    const request = await axios.post('/login' + (isInQA ? '?inQA=true' : ''), {
                        SAMLRequest: samlRequest,
                        appId,
                        email,
                        password,
                        totp,
                        recovery,
                        RelayState: relayState,
                        captchaToken: captchaResponse
                    });
                    if (request.status === 200 && request.data) {
                        setResponseData(request.data);
                    }
                    localStorage.removeItem('loginAttempt');
                } catch (err) {
                    logService.error(err);
                    let title = '';
                    const messages = [];
                    if (err?.response?.data?.error?.code === ERRORS_CODE.userNotConfirmedException.code) {
                        title = 'Account Confirmation Required';
                        messages.push(
                            <>
                                <span>
                                    Your account isn't confirmed yet. Please check your email for our confirmation link
                                    or{' '}
                                </span>
                                <b>
                                    <Link
                                        to={'/confirmEmail' + (location?.search || '')}
                                        state={{ email, tryResend: true }}
                                    >
                                        click here
                                    </Link>
                                </b>
                                <span> to resend a confirmation link.</span>
                            </>
                        );
                    } else if (err?.response?.data?.error?.code === ERRORS_CODE.passwordResetRequiredException.code) {
                        messages.push(
                            <>
                                <span>
                                    We are on a new login system since 5 AM on October 30, 2020 (GMT). If you haven't,
                                    please{' '}
                                </span>
                                <b>
                                    <Link to='/resetPassword' state={{ email }}>
                                        reset your password
                                    </Link>
                                </b>
                                <span>.</span>
                            </>
                        );
                        messages.push(
                            <>
                                <span>If urgent, please </span>
                                <a href='mailto:login-issues@opswat.com'>email</a>
                                <span> or </span>
                                <a href='https://www.opswat.com/support/contact-support'>phone</a>
                                <span>.</span>
                            </>
                        );
                    } else if (err?.response?.data?.error?.code === ERRORS_CODE.notAuthorizedException.code) {
                        title = 'Authentication Failed.';
                        messages.push('Your login attempt has failed. Make sure the email and password are correct.');
                        setHighlightErrors(true);
                        if (err?.response?.data?.error?.remains) {
                            const remains = err?.response?.data?.error?.remains;
                            const storedAttempts = parseInt(localStorage.getItem('loginAttempt'));

                            storedAttempts > 1
                                ? localStorage.setItem('loginAttempt', storedAttempts + 1)
                                : localStorage.setItem('loginAttempt', 2);
                            setRemainingTries(remains);

                            if (remains > 1) {
                                messages.push(
                                    <>
                                        <span>{remains} attempts remaining.</span>
                                    </>
                                );
                            } else {
                                messages.push(
                                    <>
                                        <span>
                                            {remains} attempt remaining, you can{' '}
                                            <b>
                                                <Link to='/resetPassword' state={{ email, autoSubmit: true }}>
                                                    reset your password
                                                </Link>
                                            </b>
                                            .
                                        </span>
                                    </>
                                );
                            }
                        }
                    } else if (err?.response?.data?.error?.code === ERRORS_CODE.captchaError.code) {
                        setCaptchaResponse(undefined);
                        setRemainingTries(1);
                        messages.push(
                            <>
                                <span>{ERRORS_CODE.captchaError.message}</span>
                            </>
                        );
                    } else if (err?.response?.data?.error?.code === ERRORS_CODE.blockUserError.code) {
                        const minutes = moment.duration(err?.response?.data?.error?.lockTime).humanize();
                        setCaptchaResponse(undefined);
                        setRemainingTries(0);
                        title = 'Login blocked.';
                        messages.push(
                            <>
                                <span>
                                    Too many failed attempts, please try again after <b>{minutes}</b>.
                                </span>
                            </>
                        );
                    } else if (err?.response?.data?.error?.code === ERRORS_CODE.codeMismatchException.code) {
                        messages.push('Your TOTP is incorrect. Please try again.');
                    } else if (
                        [ERRORS_CODE.maliciousIssuerError.code, ERRORS_CODE.invalidSamlRequestError.code].includes(
                            err?.response?.data?.error?.code
                        )
                    ) {
                        messages.push('Authentication Failed.');
                        messages.push('Invalid SAML request format.');
                    } else if (
                        (err?.response?.status >= 400 && err?.response?.status < 500) ||
                        errorMessageMap(err?.response?.data?.error?.code)
                    ) {
                        // Payload Too Large
                        if (err?.response?.status === 413) {
                            clearCookie();
                            messages.push(COOKIE_ERROR_MESSAGE);
                        } else {
                            messages.push(errorMessageMap(err?.response?.data?.error?.code));
                            messages.push(errorTraceID(err, true));
                        }
                    } else {
                        title = 'Authentication Failed.';
                        messages.push(NETWORK_ERROR_MESSAGE);
                    }

                    setErrorMessages([
                        {
                            title,
                            error: err,
                            messages
                        }
                    ]);
                }
                if (recaptchaRef?.current) {
                    recaptchaRef.current.reset();
                }
                setIsLoading(false);
            })();
        },
        [appId, email, password, samlRequest, relayState, location, captchaResponse, isAfterTwoTries, isInQA]
    );

    const onInputRecoveryCodeClick = (e, isRecovery) => {
        e.preventDefault();
        setIsInputRecoveryCode(isRecovery);
        setErrorMessages([]);
        setInfoMessages([]);
        setTotp('');
        setRecoveryCode('');
    };

    const handleMfaDisableConfirm = async (e) => {
        e.preventDefault();
        if (!mfaDisableAgreed) {
            return;
        }

        setErrorMessages([]);
        setInfoMessages([]);

        try {
            setIsLoading(true);
            const response = await axios.post('/mfa/disable/request', {
                email,
                recoveryCode
            });

            if (response.status === 200 && response.data?.success) {
                setShowMfaDisableConfirm(false);
                setShowEmailSent(true);
                setMfaDisableAgreed(false);
            }
        } catch (err) {
            logService.error(err);
            setErrorMessages([
                {
                    title: err?.response?.status === 500 ? 'Error' : '',
                    error: err,
                    messages: [errorMessageMap(err?.response?.data?.error?.code) ?? COMMON_ERROR_MESSAGE]
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitRecoveryCode = async (recoveryCode) => {
        if (!Validator.isValidRecoveryCode(recoveryCode)) {
            return;
        }

        if (appId === process.env.REACT_APP_appID_Fusion) {
            onSubmit(undefined, recoveryCode);
            return;
        }

        try {
            setIsLoading(true);
            setErrorMessages([]);
            const response = await axios.post('/mfa/recovery/validate', {
                email,
                recoveryCode
            });

            if (response.status === 200 && response.data?.valid) {
                setShowMfaDisableConfirm(true);
            }
        } catch (err) {
            logService.error(err);
            setErrorMessages([
                {
                    title: '',
                    error: err,
                    messages: [errorMessageMap(err?.response?.data?.error?.code)]
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailSentClose = (e) => {
        e.preventDefault();
        setShowEmailSent(false);
        setRecoveryCode('');
        setIsInputRecoveryCode(false);
        setIsTotpChallenge(false);
        setMfaDisableAgreed(false);
        setErrorMessages([]);
        setInfoMessages([]);
    };

    const registerLink = useMemo(() => {
        const params = new URLSearchParams(location.search);
        EXCLUDED_QUERY_PARAMS.forEach((param) => params.delete(param));
        const searchString = params.toString();
        return searchString ? `/register?${searchString}` : '/register';
    }, [location.search]);

    const splitSentences = (text) => {
        return text
            .split(/([.!?]+\s+)/)
            .reduce((acc, part, index) => {
                if (index % 2 === 0) {
                    // text content
                    if (part.trim()) {
                        acc.push(part.trim());
                    }
                } else {
                    // punctuation + whitespace
                    if (acc.length > 0) {
                        acc[acc.length - 1] += part.split(/\s+/)[0]; // add the punctuation
                    }
                }
                return acc;
            }, [])
            .filter((sentence) => sentence.trim().length > 0);
    };

    const formResponseDom = useMemo(() => {
        let input = undefined;
        if (samlResponse) {
            input = [{ name: 'SAMLResponse', value: samlResponse }];
            if (relayState) {
                input.push({ name: 'RelayState', value: relayState });
            }
        }
        return <FormPost inputs={input} entityEndpoint={entityEndpoint} />;
    }, [samlResponse, relayState, entityEndpoint]);

    const formRequestDom = useMemo(
        () => <FormPost inputs={samlIdpRequest} entityEndpoint={entityEndpoint} />,
        [samlIdpRequest, entityEndpoint]
    );

    const handleCheckEmail = (e, isCheckedEmail) => {
        e.preventDefault();
        setErrorMessages([]);
        setInfoMessages([]);
        setHighlightErrors(false);
        if (isCheckedEmail) {
            onSubmit();
        } else {
            onCheckEmail(email, location.href);
        }
    };

    const loginFormDom = useMemo(() => {
        if (!isTotpChallenge) {
            const isCheckedEmail = checkedEmail && checkedEmail === email;
            const isDisabledButton =
                !email ||
                !!emailErrors.length ||
                (!password && isCheckedEmail) ||
                isLoading ||
                (!captchaResponse && isAfterTwoTries && !isInQA) ||
                isHandlingPostLogin;

            return (
                <>
                    <div className='login__form'>
                        <h3>Sign In</h3>
                        <div>
                            <CustomInputV2
                                id='email'
                                name='email'
                                type='text'
                                label='Email'
                                placeholder='Enter your email'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && emailErrors.length === 0) {
                                        handleCheckEmail(e, isCheckedEmail);
                                    }
                                }}
                                errors={emailErrors}
                                highlightErrors={highlightErrors}
                                endAdornment={<span onClick={(e) => clearEmail(e)}>Change</span>}
                                showEndAdornment={isCheckedEmail}
                                autoFocus={!isCheckedEmail}
                                disabled={!!(isLoading || isCheckedEmail)}
                            />
                            {isCheckedEmail && (
                                <CustomInputV2
                                    autoFocus
                                    id='password'
                                    name='password'
                                    type='password'
                                    label='Password'
                                    placeholder='Enter your password'
                                    highlightErrors={highlightErrors}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && emailErrors.length === 0) {
                                            handleCheckEmail(e, isCheckedEmail);
                                        }
                                    }}
                                    rightLabel={
                                        <Link
                                            to={'/resetPassword' + QueryParametersParser.toString(query)}
                                            state={{ email }}
                                        >
                                            Forgot Password?
                                        </Link>
                                    }
                                />
                            )}
                        </div>
                        {isAfterTwoTries && (
                            <div className='login__form-captcha'>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={process.env.REACT_APP_GRC_SITE_KEY}
                                    onChange={(response) => setCaptchaResponse(response)}
                                />
                            </div>
                        )}
                        <CustomButtonV2
                            disabled={isDisabledButton}
                            loading={isLoading || isHandlingPostLogin}
                            onClick={(e) => {
                                handleCheckEmail(e, isCheckedEmail);
                            }}
                        >
                            Sign In
                        </CustomButtonV2>
                        {!_isEmpty(infoMessages) && _isEmpty(errorMessages) && (
                            <CustomSnackbarV2 messageList={infoMessages} type='success' />
                        )}
                        {!_isEmpty(errorMessages) && <CustomSnackbarV2 messageList={errorMessages} />}
                    </div>
                    <div className='login__footer'>
                        <span>Don't have an OPSWAT Account?</span>
                        <Link to={registerLink}>Create new</Link>
                    </div>
                </>
            );
        }

        const isDisabledButton =
            (!isInputRecoveryCode && !Validator.isValidTotp(totp)) ||
            (isInputRecoveryCode && !Validator.isValidRecoveryCode(recoveryCode)) ||
            isLoading ||
            isHandlingPostLogin;

        let formComponent;
        if (!isInputRecoveryCode) {
            formComponent = (
                <div className='login__form'>
                    <CustomTotpInputV2
                        autoFocus
                        label='TOTP'
                        value={totp}
                        onChange={(evt) => {
                            const newTotp = evt.target.value.replace(/[\s_]/g, '');
                            setTotp(newTotp);
                            if (Validator.isValidTotp(newTotp) && newTotp !== totp) {
                                onSubmit(newTotp);
                            }
                        }}
                        disabled={isLoading}
                    />
                    <CustomButtonV2
                        disabled={isDisabledButton}
                        loading={isLoading || isHandlingPostLogin}
                        onClick={(e) => {
                            e.preventDefault();
                            onSubmit(totp);
                        }}
                    >
                        Submit
                    </CustomButtonV2>
                    {(!_isEmpty(infoMessages) || !_isEmpty(errorMessages)) && (
                        <div>
                            <CustomSnackbarV2 messageList={infoMessages} type='success' />
                            <CustomSnackbarV2 messageList={errorMessages} />
                        </div>
                    )}
                </div>
            );
        } else {
            formComponent = (
                <div className='login__form'>
                    <CustomTotpInputV2
                        autoFocus
                        mask='*****-*****'
                        label='Recovery Code'
                        value={recoveryCode}
                        onChange={(e) => {
                            const newRecoveryCode = e.target.value.replace(/[\s_]/g, '');
                            setRecoveryCode(newRecoveryCode);
                        }}
                        onKeyDown={(e) => {
                            const newRecoveryCode = e.target.value.replace(/[\s_]/g, '');
                            if (e.key === 'Enter') {
                                handleSubmitRecoveryCode(newRecoveryCode);
                            }
                        }}
                        disabled={isLoading}
                    />
                    <CustomButtonV2
                        disabled={isDisabledButton}
                        loading={isLoading || isHandlingPostLogin}
                        onClick={(e) => {
                            e.preventDefault();
                            handleSubmitRecoveryCode(recoveryCode);
                        }}
                    >
                        Submit
                    </CustomButtonV2>
                    {(!_isEmpty(infoMessages) || !_isEmpty(errorMessages)) && (
                        <div>
                            <CustomSnackbarV2 messageList={infoMessages} type='success' />
                            <CustomSnackbarV2 messageList={errorMessages} />
                        </div>
                    )}
                </div>
            );
        }

        // Show MFA disable confirmation step
        if (showMfaDisableConfirm) {
            return (
                <div className='login__form'>
                    <h3>Disable MFA</h3>
                    <div className='login__form-content required'>
                        <p> Using a recovery code will turn off MFA on this account. </p>
                        <div className='login__form-checkbox'>
                            <input
                                type='checkbox'
                                id='confirmMfaDisable'
                                name='confirmMfaDisable'
                                checked={mfaDisableAgreed}
                                onChange={(e) => setMfaDisableAgreed(e.target.checked)}
                                disabled={isLoading}
                            />
                            <label htmlFor='confirmMfaDisable' className='required'>
                                I understand MFA will be disabled and I must re-enable it after sign-in.
                            </label>
                        </div>
                    </div>
                    <CustomButtonV2
                        disabled={!mfaDisableAgreed || isLoading}
                        loading={isLoading}
                        onClick={handleMfaDisableConfirm}
                        color='danger'
                    >
                        Disable MFA & Sign in
                    </CustomButtonV2>
                    <div className='login__footer'>
                        <span>
                            <a
                                href='/login'
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowMfaDisableConfirm(false);
                                    setMfaDisableAgreed(false);
                                    setShowEmailSent(false);
                                    setErrorMessages([]);
                                    setInfoMessages([]);
                                }}
                            >
                                Cancel
                            </a>
                        </span>
                    </div>
                    {(!_isEmpty(infoMessages) || !_isEmpty(errorMessages)) && (
                        <div>
                            <CustomSnackbarV2 messageList={infoMessages} type='success' />
                            <CustomSnackbarV2 messageList={errorMessages} />
                        </div>
                    )}
                </div>
            );
        }

        // Show email sent confirmation message
        if (showEmailSent) {
            return (
                <div className='login__form'>
                    <h3>Email confirmation</h3>
                    <div className='login__form-content'>
                        <p> For security reasons, we’ve sent a confirmation email to your email address. </p>
                        <p>
                            The confirmation link is <strong>valid for 10 minutes only</strong>. Please click it to
                            confirm disabling MFA and complete sign-in
                        </p>
                    </div>
                    <CustomButtonV2 onClick={handleEmailSentClose}>Back to Sign in</CustomButtonV2>
                </div>
            );
        }

        return (
            <div className='login__form-totp'>
                {formComponent}
                {!isInputRecoveryCode ? (
                    <div className='login__footer'>
                        <span>Can't login?</span>
                        <span>
                            <a href='/' onClick={(e) => onInputRecoveryCodeClick(e, true)}>
                                Use recovery code
                            </a>
                            , or <a href='mailto:login-issues@opswat.com'>email</a>,{' '}
                            <a
                                href='https://www.opswat.com/support/contact-support'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                phone
                            </a>{' '}
                            to us.
                        </span>
                    </div>
                ) : (
                    <div className='login__footer'>
                        <span>
                            <a href='/' onClick={(e) => onInputRecoveryCodeClick(e, false)}>
                                Back to TOTP Input
                            </a>
                            .
                        </span>
                    </div>
                )}
            </div>
        );
    }, [
        onSubmit,
        email,
        emailErrors,
        password,
        checkedEmail,
        isLoading,
        isTotpChallenge,
        isInputRecoveryCode,
        totp,
        recoveryCode,
        location,
        query,
        captchaResponse,
        isAfterTwoTries,
        isInQA,
        isHandlingPostLogin,
        infoMessages,
        errorMessages,
        registerLink,
        showMfaDisableConfirm,
        showEmailSent,
        mfaDisableAgreed,
        samlRequest,
        appId,
        relayState
    ]);

    useEffect(() => {
        if (query?.app) {
            setSamlRequest(query?.SAMLRequest);
            setRelayState(query?.RelayState);
        }

        if (!location?.state?.notLoadCurrentUser) {
            checkCurrentUser(query?.SAMLRequest, query?.app, query?.RelayState);
        }

        // Show error if there any error from IDP login
        if (query.idpErrorCode) {
            const idpErrorMessage = idpErrors(parseInt(query.idpErrorCode), query.idpIssuerId);
            if (idpErrorMessage) {
                setErrorMessages([
                    {
                        title: 'Error',
                        error: query.idpErrorTraceId,
                        messages: [idpErrorMessage]
                    }
                ]);
            }
        }

        // Show initial info
        if (query.infoMessage) {
            const sentences = splitSentences(query.infoMessage);

            setInfoMessages([
                {
                    messages: sentences
                }
            ]);
        }

        // Show error if there any error when user accepts invitation from organization
        if (query.orgAcceptInviteErrorCode) {
            setErrorMessages([
                {
                    title: 'Error',
                    messages: [
                        organizationErrors.acceptInviteErrors(parseInt(query.orgAcceptInviteErrorCode)),
                        query.orgAcceptInviteErrorTrace || undefined
                    ]
                }
            ]);
        }

        // Show error if there any error when user activate account
        if (query.activateErrorCode) {
            const appName = getAppNameByID(query.app);
            // eslint-disable-next-line no-prototype-builtins
            const productName =
                appName && productData.hasOwnProperty(appName)
                    ? productData[appName].shortname
                    : productData.OCM.shortname;

            setErrorMessages([
                {
                    title: 'Error',
                    messages: [
                        <>
                            <span>There was an error activating your OPSWAT {productName} account. Please try to </span>
                            <b>
                                <Link to={'/confirmEmail' + (location?.search || '')} state={{ tryResend: true }}>
                                    resend the confirmation email
                                </Link>
                            </b>
                            <span>.</span>
                        </>,
                        query.activateErrorTraceID ? (
                            <span>
                                If the problem persists, please contact the support team and provide the following ID:{' '}
                                <b>{query.activateErrorTraceID}</b>.
                            </span>
                        ) : (
                            'If the problem persists, please contact the support team.'
                        )
                    ]
                }
            ]);
        }

        if (autoLogin) {
            setResponseData({ user: location?.state?.user, loginResponse: location?.state?.loginResponse });
        }
    }, [query, location, autoLogin]);

    useEffect(() => {
        if (responseData) {
            enableLogging && console.log('🔐 [POST-LOGIN] Starting post-login flow', { hasUser: !!responseData.user });

            if (responseData.user) {
                enableLogging &&
                    console.log('✅ [POST-LOGIN] User data received', {
                        userId: responseData.user.user_id,
                        challengeName: responseData.user.challengeName,
                        isConsent: responseData.user.isConsent,
                        isMfaForced: responseData.user.isMfaForced
                    });

                setIsHandlingPostLogin(true);

                if (responseData.user.challengeName === 'TOTP') {
                    enableLogging && console.log('🔢 [TOTP-CHALLENGE] TOTP challenge required, showing TOTP input');
                    setIsTotpChallenge(true);
                    setIsHandlingPostLogin(false);
                } else {
                    enableLogging && console.log('🎯 [POST-LOGIN] Processing successful login');

                    // post a message for the unified navigation layout to reload the other tabs
                    enableLogging && console.log('📢 [PARENT-MESSAGE] Posting login event to parent window');
                    postLoginToParent({ event: 'USER_LOGIN_SSO' });

                    if (responseData.user.user_id) {
                        enableLogging && console.log('📊 [GTM] Sending login success event to GTM', { appId });
                        gtmService.sendLoginSuccessEvent(responseData.user, appId);
                    }

                    const appName = getAppNameByID(appId);
                    enableLogging && console.log('🏷️ [APP-INFO] Retrieved app name', { appId, appName });

                    const userConsent = responseData.user.isConsent;
                    if (!userConsent) {
                        enableLogging && console.log('⚠️ [CONSENT] User consent required, redirecting to consent page');
                        navigate(
                            '/consent' + (query.redirect ? `?redirect=${encodeURIComponent(query.redirect)}` : ''),
                            {
                                state: {
                                    appId,
                                    samlRequest,
                                    relayState,
                                    appName,
                                    consent: userConsent
                                }
                            }
                        );
                        return;
                    }

                    enableLogging && console.log('✅ [CONSENT] User consent already given');

                    if (responseData.loginResponse?.context && responseData.loginResponse?.entityEndpoint) {
                        enableLogging &&
                            console.log('🔗 [SAML] SAML response detected', {
                                hasContext: !!responseData.loginResponse.context,
                                hasEndpoint: !!responseData.loginResponse.entityEndpoint,
                                acsMethod: responseData.loginResponse.acsMethod
                            });

                        // Redirect to ACS url if acsMethod = 'GET'
                        if (responseData.loginResponse.acsMethod === 'GET') {
                            const redirectUrl = `${responseData.loginResponse.entityEndpoint}/SAMLResponse=${responseData.loginResponse.context}`;
                            enableLogging &&
                                console.log('🔀 [REDIRECT-GET] Redirecting via GET method', { redirectUrl });
                            window.location.href = redirectUrl;
                        } else {
                            // If login success with SAMLRequest from App
                            enableLogging &&
                                console.log('📮 [REDIRECT-POST] Preparing POST redirect with SAML response');
                            setSamlResponse(responseData.loginResponse.context);
                            setEntityEndpoint(responseData.loginResponse.entityEndpoint);
                        }
                        return;
                    }

                    if (autoLogin) {
                        enableLogging && console.log('🤖 [AUTO-LOGIN] Auto-login mode detected', { app: query.app });
                        if (query.app === process.env.REACT_APP_appID_MA) {
                            const redirectUrl = `${process.env.REACT_APP_app_href_MA}/console/saml/login`;
                            enableLogging && console.log('🔀 [REDIRECT-MA] Redirecting to MA console', { redirectUrl });
                            window.location.href = redirectUrl;
                        } else {
                            enableLogging &&
                                console.log('🔀 [REDIRECT-FUSION] Redirecting to Fusion SSO', {
                                    redirectUrl: process.env.REACT_APP_app_href_sso_Fusion
                                });
                            window.location.href = process.env.REACT_APP_app_href_sso_Fusion;
                        }
                    } else if (query.redirect && isWhiteListed(query.redirect)) {
                        enableLogging &&
                            console.log('🔀 [REDIRECT-WHITELIST] Redirecting to whitelisted URL', {
                                redirectUrl: query.redirect
                            });
                        window.location.href = query.redirect;
                    } else if (responseData.user.isMfaForced || process.env.REACT_APP_ENVIRONMENT === 'local') {
                        // If user should be setting MFA before login app, or local env
                        enableLogging &&
                            console.log('🔒 [REDIRECT-PROFILE] Redirecting to profile', {
                                reason: responseData.user.isMfaForced ? 'MFA forced' : 'Local environment',
                                isMfaForced: responseData.user.isMfaForced,
                                environment: process.env.REACT_APP_ENVIRONMENT
                            });
                        window.location.href = '/profile';
                    } else if (query.app === process.env.REACT_APP_appID_SKL && !query.redirect) {
                        const redirectUrl = `${process.env.REACT_APP_app_href_SKL}?utm_source=direct-traffic&utm_medium=login-register-process`;
                        enableLogging &&
                            console.log('🔀 [REDIRECT-SKL] Redirecting to SKL with UTM params', { redirectUrl });
                        window.location.href = redirectUrl;
                    } else {
                        // If not login from any App, redirect to My OPSWAT as default app for Staging or Production environment
                        if (['staging', 'prod'].includes(process.env.REACT_APP_ENVIRONMENT)) {
                            const redirectUrl = `${process.env.REACT_APP_app_href_OCM}/`;
                            enableLogging &&
                                console.log('🔀 [REDIRECT-OCM] Redirecting to My OPSWAT (default)', {
                                    environment: process.env.REACT_APP_ENVIRONMENT,
                                    redirectUrl
                                });
                            window.location.href = redirectUrl;
                        } else {
                            // Redirect to the profile page for other environments since there are only Staging and Prod environments for My OPSWAT
                            enableLogging &&
                                console.log('🔀 [REDIRECT-PROFILE] Redirecting to profile (non-prod default)', {
                                    environment: process.env.REACT_APP_ENVIRONMENT
                                });
                            window.location.href = '/profile';
                        }
                    }
                }
            } else {
                enableLogging && console.log('❌ [POST-LOGIN] No user data in response');
                setIsLoadingCurrentUser(false);
            }
        }
    }, [responseData, appId, samlRequest, relayState, query, autoLogin]);

    const { pageTitle, pageDescription } = usePageMetadata(appName);

    return (
        <>
            <CommonLayoutV2
                pageTitle={pageTitle}
                pageDescription={pageDescription}
                isLoading={isLoadingCurrentUser || isHandlingPostLogin}
            >
                <div className='login__container'>{loginFormDom}</div>
            </CommonLayoutV2>
            {formResponseDom}
            {formRequestDom}
        </>
    );
};

export default LoginPageV2;

LoginPageV2.propTypes = {
    location: PropTypes.object
};
